/*
 * Feature: redesign-login-page
 * See: docs/features/redesign-login-page.md
 * Feature: org-specific-home-pages
 * See: docs/features/org-specific-home-pages.md
 */

// ./loginpage.js
import React from 'react';
import {
  Box, Button, Text, Grid, GridItem, Flex, VStack, HStack,
  Heading, Card, CardHeader, CardBody, Divider,
  useToast, Tag, TagLabel, Badge, Spacer, Menu, MenuButton,
  MenuList, MenuItem, Image, IconButton, Table, Thead, Tbody, Tr, Th, Td,
  Progress
} from '@chakra-ui/react';
import { AddIcon, AttachmentIcon, CheckCircleIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useEffect } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useAuth0 } from "@auth0/auth0-react";

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const toast = useToast();


  useEffect(() => {
    // Get error parameters from URL
    const errorCode = searchParams.get('error_code');
    const errorMessage = searchParams.get('error_message');
    
    // Display error message if present
    if (errorCode && errorMessage) {
      toast({
        title: "Account Status",
        description: decodeURIComponent(errorMessage),
        status: "warning",
        duration: 9000,
        isClosable: true,
        position: "top"
      });
      
      // Optionally, you can clear the URL parameters after displaying the toast
      // This requires useNavigate from react-router-dom
      // const navigate = useNavigate();
      // navigate('/login', { replace: true });
    }
  }, [searchParams, toast]);
  
  const { loginWithRedirect } = useAuth0();

  // Menu structure from the provided JSON
  const menuStructure = {
    "logo": {"alt": "Revedy", "src": "/images/rev_logo.png", "path": "/home", "height": "25px"}, 
    "items": [
      {"CPT Coding": {"sub": [{"CPT Coding": "/search"}, {"Medical Necessity Analysis": "/medical-necessity-analysis"}, {"My Encounters": "/encounters"}, {"All Encounters": "/encountersadmin"}]}}, 
      {"ICD-10": {"sub": [{"Code ICD-10": "/note"}]}}, 
      {"Scribe": {"sub": [{"Try It Now": "/scribe"}]}}, 
      {"IONM": {"sub": [{"Overview": "/ionm-docs"}, {"Cases Dashboard": "/ionmtable"}, {"New IONM Case": "/ionmcase"}, {"Medical Necessity Analysis": "/medical-necessity-analysis"}]}}, 
      {"API & Documentation": {"sub": [{"API Documentation": "/api-docs"}, {"IONM API": "/apiionm"}, {"IONM API Demo": "/apiionmdemo"}, {"Encounter Analysis": "/ea"}, {"Encounter List": "/el"}, {"Generate Note": "/generate"}]}}, 
      {"Admin": {"sub": [{"Client Admin": "/client-admin"}, {"Template Manager": "/templatemanager"}, {"Log Viewer": "/logviewer"}, {"File Upload": "/securefiles"}, {"Admin Panel": "/admin"}, {"Chat Assistant": "/chat"}]}}
    ]
  };

  const handleSignup = () => {
    if (!window.env.REACT_APP_REVEDY_API_HOST) {
      console.error('REACT_APP_REVEDY_API_HOST is not set');
      return;
    }
  
    loginWithRedirect({
      authorizationParams: {
        'screen_hint': 'signup',
      },
      appState: { returnTo: `${window.location.origin}/` },
      audience: `${window.env.REACT_APP_AUTH0_AUDIENCE}`,
      scope: "read:current_user",
    });
  };

  const handleLogin = () => {
    if (!window.env.REACT_APP_REVEDY_API_HOST) {
      console.error('REACT_APP_REVEDY_API_HOST is not set');
      return;
    }

    loginWithRedirect({
      appState: { returnTo: `${window.location.origin}/` },
      audience: `${window.env.REACT_APP_AUTH0_AUDIENCE}`,
      scope: "read:current_user",
    });
  };

  // Generate current dates for mock data
  const generateMockCases = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

    const formatDateTime = (date, hours, minutes) => {
      const d = new Date(date);
      d.setHours(hours, minutes, 0, 0);
      return d.toISOString().slice(0, 19).replace('T', ' ');
    };

    return [
      { id: 1, external_case_id: "DEMO-001", status: "complete", date_updated: formatDateTime(yesterday, 14, 30), case_type: "Surgery", case_description: "Total Hip Replacement" },
      { id: 2, external_case_id: "DEMO-002", status: "in_progress", date_updated: formatDateTime(today, 10, 15), case_type: "Surgery", case_description: "Laparoscopic Cholecystectomy" },
      { id: 3, external_case_id: "DEMO-003", status: "pending", date_updated: formatDateTime(twoDaysAgo, 16, 45), case_type: "Surgery", case_description: "Carpal Tunnel Release" }
    ];
  };

  // Mock data for the preview
  const mockCases = generateMockCases();

  // Helper function to format date in a more readable way
  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    
    const parts = dateString.split(' ');
    if (parts.length !== 2) return dateString;
    
    const datePart = parts[0];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const [year, month, day] = datePart.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    
    if (dateObj.getTime() === today.getTime()) {
      return 'Today';
    } else if (dateObj.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      return `${month}/${day}/${year.toString().substr(2)}`;
    }
  };

  // Function to render menu items based on the structure
  const renderMenuItems = () => {
    return menuStructure.items.map((item, index) => {
      const [label, value] = Object.entries(item)[0];
      
      if (typeof value === 'string') {
        return (
          <Text 
            key={index}
            fontSize="sm" 
            textDecoration="underline" 
            color="gray.700" 
            cursor="pointer" 
            onClick={handleSignup}
          >
            {label}
          </Text>
        );
      }
      
      if (typeof value === 'object' && value.sub) {
        return (
          <Menu key={index}>
            <MenuButton as={Text} fontSize="sm" textDecoration="underline" color="gray.700">
              {label}
              <ChevronDownIcon ml={1} />
            </MenuButton>
            <MenuList>
              {value.sub.map((subItem, subIndex) => {
                const [subLabel, subPath] = Object.entries(subItem)[0];
                return (
                  <MenuItem 
                    key={subIndex} 
                    fontSize="sm" 
                    onClick={handleSignup}
                  >
                    {subLabel}
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
        );
      }
      
      return null;
    });
  };

  

  // Render status indicator based on case status
  const renderStatusIndicator = (status) => {
    if (!status) return null;
    
    if (status.toLowerCase() === 'in_progress') {
      return (
        <Box as="span" w="10px" h="10px" borderRadius="50%" bg="orange.500" mr={1} />
      );
    } else if (status.toLowerCase() === 'complete') {
      return (
        <Box as="span" w="10px" h="10px" borderRadius="50%" bg="green.500" mr={1} />
      );
    }
    return null;
  };

  return (    
    <Box textAlign="center" fontSize="md" bg="white">
      <Grid
        templateAreas={`"header"
                        "main"
                        "footer"`}
        gridTemplateRows={'60px 1fr 30px'}
        gridTemplateColumns={'1fr'}
        h='100vh'
        gap='1'
        color='blackAlpha.800'
        fontWeight='bold'
      >
        {/* Header with menu */}
        <GridItem area="header" bg="white" borderBottom="1px" borderColor="gray.200">
          <Flex px={6} h="100%" alignItems="center" bg="header_background" borderBottom="1px" borderColor="gray.200">
            {/* Logo and Tagline */}
            <Flex alignItems="center" mr={8}>
              <Image 
                src={menuStructure.logo.src}
                alt={menuStructure.logo.alt}
                height={menuStructure.logo.height}
                onClick={handleSignup}
                cursor="pointer"
              />
              <Text 
                ml={3} 
                fontSize="sm" 
                color="gray.600" 
                fontStyle="italic"
              >
                Your AI Agent for RCM
              </Text>
            </Flex>
            
            {/* Navigation Menu with consistent styling */}
            <Flex style={{ gap: '20px' }}>
              {renderMenuItems()}
            </Flex>
            
            <Spacer />
            
            <HStack spacing={4}>
              <Button 
                colorScheme="blue" 
                variant="outline" 
                size="sm"
                onClick={handleLogin}
              >
                Log In
              </Button>
            </HStack>
          </Flex>
        </GridItem>

        {/* Main content area with CPT Coder preview */}
        <GridItem 
          area="main" 
          bg='white' 
          p={6} 
          textAlign="left" 
          alignContent={"left"}
          overflowY="auto"
        >
          {/* Hero Section */}
          <Box mb={8} textAlign="center" py={6}>
            <Heading size="xl" mb={4}>Revedy AI Agent for Revenue Cycle Management</Heading>
            <Text fontSize="lg" maxW="700px" mx="auto" color="gray.600" mb={6}>
              Our AI-powered Coding Agent helps physicians and revenue cycle leaders save time, 
              improve accuracy, and maximize reimbursement.
            </Text>
            <Button 
              colorScheme="blue" 
              variant="solid" 
              size="lg"
              fontSize="lg"
              px={10}
              py={7}
              onClick={handleSignup}
              _hover={{ transform: 'scale(1.02)', boxShadow: 'lg' }}
              transition="all 0.2s"
            >
              Access Live Demo
            </Button>
            <Text fontSize="sm" color="gray.500" mt={3}>
              All we need is your email address. No credit card required.
            </Text>
          </Box>

          {/* App Preview Section Header */}
          <Box mb={6} pb={4} borderBottom="1px" borderColor="gray.200" textAlign="center">
            <Heading size="lg" mb={2}>See the Platform in Action</Heading>
            <Text fontSize="md" color="gray.600">
              Here's what you'll get access to:
            </Text>
          </Box>

          <Flex>
            {/* Left Column - Case List */}
            <Box width="280px" pr={4} borderRightWidth="1px">
              {/* New Case Button */}
              <Button 
                leftIcon={<AddIcon />} 
                colorScheme="green" 
                variant="solid" 
                size="md" 
                width="100%" 
                mb={4}
                onClick={handleSignup}
              >
                New Case
              </Button>
              
              <Heading size="md" mb={4}>Cases</Heading>

              <VStack spacing={3} align="stretch" maxH="calc(100vh - 400px)" overflowY="auto">
                {mockCases.map(caseItem => (
                  <Box 
                    key={caseItem.id}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    bg={caseItem.id === 2 ? "green.50" : "white"}
                    borderColor={caseItem.id === 2 ? "green.400" : "gray.200"}
                    cursor="pointer"
                    onClick={handleSignup}
                    _hover={{ 
                      bg: caseItem.id === 2 ? "green.100" : "gray.50",
                      borderColor: caseItem.id === 2 ? "green.500" : "gray.300"
                    }}
                    transition="all 0.2s"
                  >
                    <HStack mb={1}>
                      {renderStatusIndicator(caseItem.status)}
                      
                      <Text fontWeight="bold" fontSize="sm">
                        {caseItem.case_description}
                      </Text>
                    </HStack>
                    
                    <Text fontSize="xs" color="gray.600" mb={2}>
                      {formatDateShort(caseItem.date_updated)} - {caseItem.external_case_id}
                    </Text>
                    
                    <HStack spacing={2} mt={1}>
                      <Badge 
                        colorScheme={caseItem.status === 'complete' ? "green" : 
                                    caseItem.status === 'in_progress' ? "orange" : "gray"} 
                        fontSize="xs"
                      >
                        {caseItem.status}
                      </Badge>
                      <Badge colorScheme="purple" fontSize="xs">{caseItem.case_type}</Badge>
                      <Badge colorScheme="blue" fontSize="xs">2 files</Badge>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>

            {/* Middle Column - Results Showcase */}
            <Box flex="1" pl={4} pr={4}>
              <VStack spacing={4} align="stretch">
                {/* CPT Coding Results Card */}
                <Card variant="outline" borderColor="blue.200">
                  <CardHeader p={3} bg="blue.50">
                    <Text fontWeight="bold" fontSize="md">CPT Coding Results</Text>
                    {/* Workflow Tabs Display */}
                    <HStack spacing={1} mt={2} flexWrap="wrap">
                      {['Standard CPT', 'ICD-10 Codes', 'Assign Payer', 'Final CPT', 'Medical Necessity', 'Payer Response', 'Coder Notes'].map((tab, index) => (
                        <Tag 
                          key={index} 
                          size="sm" 
                          variant={index === 0 ? "solid" : "outline"} 
                          colorScheme="blue"
                          cursor="pointer"
                          onClick={handleSignup}
                        >
                          <TagLabel fontSize="xs">{tab}</TagLabel>
                        </Tag>
                      ))}
                    </HStack>
                  </CardHeader>
                  <CardBody p={4}>
                    {/* CPT Code Display */}
                    <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50" mb={4}>
                      <Flex justify="space-between" align="flex-start" mb={3}>
                        <Box>
                          <HStack>
                            <Badge colorScheme="green" fontSize="lg" px={2} py={1}>99213</Badge>
                            <Badge colorScheme="blue" size="sm">Billable</Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.700" mt={2} maxW="400px">
                            Office or other outpatient visit for the evaluation and management of an established patient, which requires a medically appropriate history and/or examination and low level of medical decision making.
                          </Text>
                        </Box>
                        <VStack align="flex-end" spacing={1}>
                          <Text fontSize="xs" color="gray.500">Work RVU: <strong>1.3</strong></Text>
                          <Text fontSize="xs" color="gray.500">Units: <strong>1</strong></Text>
                        </VStack>
                      </Flex>
                      
                      <Divider my={3} />
                      
                      <HStack spacing={6} mb={3}>
                        <Box>
                          <Text fontSize="xs" color="gray.500">Facility Reimbursement</Text>
                          <Text fontSize="md" fontWeight="bold" color="green.600">$66.66</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">Non-Facility Reimbursement</Text>
                          <Text fontSize="md" fontWeight="bold" color="green.600">$92.84</Text>
                        </Box>
                      </HStack>
                      
                      <Box bg="white" p={3} borderRadius="md" borderWidth="1px">
                        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>JUSTIFICATION</Text>
                        <Text fontSize="sm" color="gray.700">
                          E/M code 99213 selected based on comprehensive MDM assessment of 'low'.
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          <strong>Discussion:</strong> Low MDM determined by all three pillars scoring low. Key evidence indicates stable chronic surveillance (Pillar 1 low); no documentation of data review beyond standard procedure (Pillar 2 low); encounter assessed as low-risk per specific guidance (Pillar 3 low).
                        </Text>
                      </Box>
                    </Box>
                  </CardBody>
                </Card>

                {/* Medical Necessity Analysis Card */}
                <Card variant="outline" borderColor="green.200">
                  <CardHeader p={3} bg="green.50">
                    <Flex justify="space-between" align="center">
                      <Text fontWeight="bold" fontSize="md">Medical Necessity Analysis</Text>
                      <Badge colorScheme="green">AI-Powered</Badge>
                    </Flex>
                  </CardHeader>
                  <CardBody p={4}>
                    {/* Score Display */}
                    <Flex justify="space-between" align="center" mb={4}>
                      <Box flex="1" mr={4}>
                        <Text fontSize="sm" color="gray.600" mb={1}>Approval Confidence Score</Text>
                        <Progress value={85} colorScheme="green" size="lg" borderRadius="md" />
                      </Box>
                      <Box textAlign="right">
                        <Text fontSize="2xl" fontWeight="bold" color="green.500">85/100</Text>
                        <Badge colorScheme="green" fontSize="sm">HIGH APPROVAL</Badge>
                      </Box>
                    </Flex>
                    
                    <Box bg="green.50" p={3} borderRadius="md" mb={4}>
                      <HStack>
                        <CheckCircleIcon color="green.500" />
                        <Text fontSize="sm" fontWeight="medium" color="green.700">
                          Ready to Submit - Optimized codes are well-supported
                        </Text>
                      </HStack>
                    </Box>

                    {/* Recommendations */}
                    <Box mb={4}>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={2}>AI Recommendations</Text>
                      <VStack align="stretch" spacing={2}>
                        <Box p={2} bg="gray.50" borderRadius="md" borderLeftWidth="3px" borderLeftColor="blue.400">
                          <Text fontSize="xs">Add Z86.010 as primary ICD-10 to justify E/M for surveillance of personal history of adenomatous colonic polyps.</Text>
                        </Box>
                        <Box p={2} bg="gray.50" borderRadius="md" borderLeftWidth="3px" borderLeftColor="blue.400">
                          <Text fontSize="xs">Include K57.30 (diverticulosis of large intestine w/o complications) as secondary for documented sigmoid diverticula.</Text>
                        </Box>
                        <Box p={2} bg="gray.50" borderRadius="md" borderLeftWidth="3px" borderLeftColor="blue.400">
                          <Text fontSize="xs">Add K63.5 (polyp of colon) for procedural finding; await pathology for specificity upgrade.</Text>
                        </Box>
                      </VStack>
                    </Box>

                    {/* Diagnosis Evidence Table */}
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={2}>Diagnoses with Evidence</Text>
                      <Table size="sm" variant="simple">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th fontSize="xs">Code</Th>
                            <Th fontSize="xs">Description</Th>
                            <Th fontSize="xs" isNumeric>Confidence</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td><Badge colorScheme="purple" fontSize="xs">K57.30</Badge></Td>
                            <Td fontSize="xs">Diverticulosis of large intestine without perforation...</Td>
                            <Td isNumeric><Badge colorScheme="green">9/10</Badge></Td>
                          </Tr>
                          <Tr>
                            <Td><Badge colorScheme="purple" fontSize="xs">K63.5</Badge></Td>
                            <Td fontSize="xs">Polyp of colon</Td>
                            <Td isNumeric><Badge colorScheme="green">9/10</Badge></Td>
                          </Tr>
                          <Tr>
                            <Td><Badge colorScheme="purple" fontSize="xs">Z12.11</Badge></Td>
                            <Td fontSize="xs">Encounter for screening for malignant neoplasm of colon</Td>
                            <Td isNumeric><Badge colorScheme="green">8/10</Badge></Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  </CardBody>
                </Card>

                {/* CTA Card */}
                <Card variant="outline" borderColor="blue.300" bg="blue.50">
                  <CardBody p={4} textAlign="center">
                    <Text fontSize="md" fontWeight="medium" mb={3}>
                      Ready to see this level of detail for your cases?
                    </Text>
                    <Button 
                      colorScheme="blue" 
                      size="lg"
                      onClick={handleSignup}
                      _hover={{ transform: 'scale(1.02)', boxShadow: 'md' }}
                      transition="all 0.2s"
                    >
                      Access Live Demo
                    </Button>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      No credit card required
                    </Text>
                  </CardBody>
                </Card>
              </VStack>
            </Box>

            {/* Right Column - Case Details */}
            <Box width="350px" pl={4} borderLeftWidth="1px">
              <Card variant="outline" height="100%">
                <CardHeader p={3} bg="blue.50">
                  <Text fontWeight="bold" fontSize="md">Case Details</Text>
                </CardHeader>
                <CardBody>
                  <Badge colorScheme="orange" mb={2}>
                    <Box as="span" w="8px" h="8px" borderRadius="50%" bg="orange.500" mr={1} display="inline-block" />
                    in_progress
                  </Badge>
                  <Text fontSize="sm"><strong>Case ID:</strong> DEMO-002</Text>
                  <Text fontSize="sm"><strong>Type:</strong> Surgery</Text>
                  <Text fontSize="sm"><strong>Created:</strong> {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</Text>
                  
                  <Box mt={4}>
                    <Heading size="sm" mb={2}>Uploaded Content</Heading>
                    <Box 
                      p={2} 
                      borderWidth="1px" 
                      borderRadius="md" 
                      cursor="pointer"
                      _hover={{ bg: "blue.50" }}
                      onClick={handleSignup}
                    >
                      <HStack>
                        <AttachmentIcon color="blue.500" />
                        <Text color="blue.600" textDecoration="underline" fontSize="sm">operative_report.pdf</Text>
                        <Tag size="sm" colorScheme="blue" borderRadius="full">
                          <TagLabel fontSize="xs">PDF</TagLabel>
                        </Tag>
                      </HStack>
                    </Box>
                  </Box>
                </CardBody>
              </Card>
            </Box>
          </Flex>
        </GridItem>

        {/* Footer */}
        <GridItem area="footer" bg='white' borderTop='1px' borderColor='gray.200' p={2}>
          <Text fontSize="xs" color="gray.500">© 2026 Revedy. All rights reserved.</Text>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default LoginPage;